import { memo } from 'react';

import type { NumberNotation } from '../../format/number';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps, isBlankNumber } from './shared';

export type NumProps = EmptyProps & {
  /** The number. `0` renders as zero, not as empty. */
  readonly value: number | null | undefined;
  /**
   * Fractional digits.
   *
   * A fixed width under `standard` and `percent`, so `2` pads `1.5` to `1.50`. Compact notation
   * reads it as a ceiling instead.
   */
  readonly digits?: number;
  /** Defaults to `standard`. */
  readonly notation?: NumberNotation;
};

/**
 * A bare number with no unit: a vehicle count, a percentage, a score.
 *
 * Named `Num` rather than `Number` so that a file importing it does not shadow the global.
 */
const Num = memo(
  ({
    value,
    digits,
    notation,
    emptyValue = DEFAULT_EMPTY_VALUE,
  }: NumProps): JSX.Element => {
    const format = useFormat();

    if (isBlankNumber(value)) return <>{emptyValue}</>;

    return <>{format.number(value, { digits, notation })}</>;
  }
);

Num.displayName = 'Num';

export default Num;
